import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDeleteInfluencers } from '@/hooks/useInfluencers';
import type { useInfluencerRefresh } from '@/hooks/useInfluencerRefresh';
import type { Influencer, InfluencerFilters, SelectedInfluencerModalObj } from '../types';

interface UseInfluencerSelectionProps {
  influencers: Influencer[];
  totalCount: number;
  searchTerm: string;
  filters: InfluencerFilters;
  refresh: ReturnType<typeof useInfluencerRefresh>;
}

export function useInfluencerSelection({
  influencers,
  totalCount,
  searchTerm,
  filters,
  refresh,
}: UseInfluencerSelectionProps) {
  const [selectedInfluencers, setSelectedInfluencers] = useState<string[]>([]);
  const [refreshingInfluencerId, setRefreshingInfluencerId] = useState<string | null>(null);
  const [showSelectInfluencersPrompt, setShowSelectInfluencersPrompt] = useState(false);

  const deleteInfluencersMutation = useDeleteInfluencers();

  const isAllCurrentPageSelected =
    influencers.length > 0 && influencers.every((inf) => selectedInfluencers.includes(inf.id));

  const isAllSelected =
    selectedInfluencers.length > 0 &&
    ((totalCount > 0 && selectedInfluencers.length >= totalCount) || isAllCurrentPageSelected);

  // Selection handlers: select all matching influencers across pages
  const handleSelectAll = async () => {
    if (selectedInfluencers.length > 0) {
      setSelectedInfluencers([]);
      setShowSelectInfluencersPrompt(false);
      return;
    }

    try {
      const fetchSize = totalCount > 0 ? totalCount : 10000;
      const { data, error } = await supabase.rpc('search_influencers', {
        p_search_term: searchTerm?.trim() || null,
        p_gender: filters.gender || null,
        p_platform: filters.platform || 'all',
        p_engagement: filters.engagement || 'all',
        p_location: filters.location || 'all',
        p_tier: filters.tier || 'all',
        p_status: filters.status || 'all',
        p_page: 1,
        p_page_size: fetchSize,
      });

      if (!error && data && Array.isArray(data)) {
        const allIds = (data as Array<{ id: string }>).map((row) => row.id);
        setSelectedInfluencers(allIds);
      } else {
        setSelectedInfluencers(influencers.map((inf) => inf.id));
      }
    } catch {
      setSelectedInfluencers(influencers.map((inf) => inf.id));
    }
    setShowSelectInfluencersPrompt(false);
  };

  const handleSelectInfluencer = (id: string) => {
    setSelectedInfluencers((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
    setShowSelectInfluencersPrompt(false);
  };

  // Refresh selected handler — clears selection on success
  const handleRefreshSelected = async () => {
    const success = await refresh.handleSelectedRefresh(selectedInfluencers);
    if (success) {
      setSelectedInfluencers([]);
    }
  };

  const handleRefreshInfluencer = async (id: string) => {
    if (refreshingInfluencerId) return;

    setRefreshingInfluencerId(id);
    try {
      await refresh.handleSingleMetricsRefresh(id);
    } finally {
      setRefreshingInfluencerId(null);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedInfluencers.length === 0) return;
    if (
      window.confirm(
        `Are you sure you want to delete the selected ${selectedInfluencers.length} influencer(s)? This action cannot be undone.`
      )
    ) {
      deleteInfluencersMutation.mutate(selectedInfluencers, {
        onSuccess: () => {
          setSelectedInfluencers([]);
        },
      });
    }
  };

  const [influencerCache, setInfluencerCache] = useState<Map<string, Influencer>>(new Map());

  // Cache visible influencers whenever page data changes
  useEffect(() => {
    if (influencers && influencers.length > 0) {
      setInfluencerCache((prev) => {
        const next = new Map(prev);
        for (const inf of influencers) {
          next.set(inf.id, inf);
        }
        return next;
      });
    }
  }, [influencers]);

  // For modal: rich influencer preview data across pages and searches
  const selectedInfluencerObjs: SelectedInfluencerModalObj[] = useMemo(() => {
    return selectedInfluencers
      .map((id) => {
        const inf = influencerCache.get(id) || influencers.find((i) => i.id === id);
        if (!inf) return null;
        return {
          id: inf.id,
          realName: inf.realName,
          handle: inf.handle,
          profilePhoto: inf.profilePhoto,
          platform: inf.platforms?.[0] || 'Instagram',
          followers: inf.followers?.instagram || inf._db?.followers_count || null,
          engagementRate: inf.engagementRate,
          videoViews: inf._db?.video_view_count || null,
          avgLikes: inf._db?.avg_likes || null,
          category: inf.tags?.[0] || 'Influencer',
          verified: inf._db?.verified ?? inf.verified ?? true,
        };
      })
      .filter((item): item is SelectedInfluencerModalObj => item !== null);
  }, [influencerCache, influencers, selectedInfluencers]);

  useEffect(() => {
    if (!showSelectInfluencersPrompt) return;

    const timeoutId = window.setTimeout(() => {
      setShowSelectInfluencersPrompt(false);
    }, 6000);

    return () => window.clearTimeout(timeoutId);
  }, [showSelectInfluencersPrompt]);

  return {
    selectedInfluencers,
    setSelectedInfluencers,
    refreshingInfluencerId,
    showSelectInfluencersPrompt,
    setShowSelectInfluencersPrompt,
    isAllSelected,
    isAllCurrentPageSelected,
    handleSelectAll,
    handleSelectInfluencer,
    handleRefreshSelected,
    handleRefreshInfluencer,
    handleDeleteSelected,
    selectedInfluencerObjs,
    clearSelection: () => setSelectedInfluencers([]),
  };
}
